import { BRAND_LEGAL_NAME, BRAND_SHORT_NAME } from '../../constants/brand.js';
import { DATA_PROTECTION_ADDRESS, DATA_PROTECTION_EMAIL } from '../../constants/legalContact.js';

/**
 * Política de tratamiento de datos personales — Ley 1581 de 2012 y Decreto 1377 de 2013 (Colombia).
 * Documento orientativo: conviene validarlo con el área jurídica de la cooperativa.
 */
export const dataTreatmentPolicy = {
  id: 'treatment',
  title: 'Política de tratamiento de datos personales',
  updatedAt: '20 de mayo de 2025',
  intro: `${BRAND_SHORT_NAME} (${BRAND_LEGAL_NAME}), en adelante «${BRAND_SHORT_NAME}» o «la Cooperativa», informa a sus asociados, empleados, proveedores, visitantes del portal web y demás titulares sobre el tratamiento de sus datos personales conforme a la legislación colombiana de protección de datos.`,
  sections: [
    {
      heading: '1. Marco normativo',
      paragraphs: [
        'Esta política se adopta en cumplimiento de la Ley 1581 de 2012, el Decreto 1377 de 2013, las circulares de la Superintendencia de Industria y Comercio (SIC) y demás normas que las modifiquen, adicionen o complementen.',
        'La Cooperativa actúa como responsable del tratamiento de datos personales recolectados en el desarrollo de su objeto social, gestión cooperativa, relación con asociados y operación del sitio web institucional.',
      ],
    },
    {
      heading: '2. Responsable del tratamiento',
      paragraphs: [
        `Responsable: ${BRAND_LEGAL_NAME} — ${BRAND_SHORT_NAME}.`,
        `Domicilio: ${DATA_PROTECTION_ADDRESS}.`,
        `Correo de contacto para asuntos de datos personales: ${DATA_PROTECTION_EMAIL}.`,
      ],
    },
    {
      heading: '3. Definiciones',
      list: [
        'Dato personal: cualquier información vinculada o que pueda asociarse a una o varias personas naturales determinadas o determinables.',
        'Titular: persona natural cuyos datos personales sean objeto de tratamiento.',
        'Tratamiento: cualquier operación sobre datos personales (recolección, almacenamiento, uso, circulación, supresión, etc.).',
        'Responsable del tratamiento: quien decide sobre la base de datos y/o el tratamiento.',
        'Encargado del tratamiento: quien realiza el tratamiento por cuenta del responsable.',
        'Autorización: consentimiento previo, expreso e informado del titular para el tratamiento.',
        'Dato sensible: información que afecta la intimidad del titular o cuyo uso indebido puede generar discriminación (salud, orientación política, convicciones religiosas, etc.).',
      ],
    },
    {
      heading: '4. Principios aplicables',
      list: [
        'Legalidad: el tratamiento se realiza conforme a la ley.',
        'Finalidad: los datos se utilizan para fines legítimos, informados al titular.',
        'Libertad: el tratamiento solo se efectúa con autorización, salvo excepciones legales.',
        'Veracidad o calidad: la información debe ser veraz, completa, exacta y actualizada.',
        'Transparencia: garantía de información al titular sobre el uso de sus datos.',
        'Acceso y circulación restringida: tratamiento con medidas de seguridad.',
        'Seguridad: protección de los datos contra riesgos de acceso no autorizado.',
        'Confidencialidad: deber de reserva de quienes intervienen en el tratamiento.',
      ],
    },
    {
      heading: '5. Finalidades del tratamiento',
      paragraphs: ['Los datos personales serán tratados, entre otras, para las siguientes finalidades:'],
      list: [
        'Gestión de la relación cooperativa: afiliación, aportes, ahorros, créditos, servicios y beneficios para asociados.',
        'Cumplimiento de obligaciones legales ante la Superintendencia de Economía Solidaria y otras autoridades.',
        'Atención de solicitudes, PQRS, reclamos y comunicaciones institucionales.',
        'Gestión de talento humano, nómina, seguridad social y obligaciones laborales.',
        'Relación con proveedores y contratistas.',
        'Operación del portal web: registro de consultas, formularios de contacto, navegación y seguridad.',
        'Envío de información sobre productos, programas, noticias y actividades de la Cooperativa, cuando exista autorización.',
        'Prevención del fraude, lavado de activos y financiación del terrorismo, conforme a la normativa aplicable.',
      ],
    },
    {
      heading: '6. Categorías de datos y titulares',
      paragraphs: [
        'Se podrán tratar datos de identificación, contacto, laborales, financieros, comerciales, biométricos cuando la ley lo permita, y demás datos necesarios para las finalidades descritas.',
        'El tratamiento de datos sensibles solo se realizará cuando exista autorización expresa del titular o habilitación legal, informando al titular que no está obligado a autorizarlos.',
      ],
    },
    {
      heading: '7. Autorización del titular',
      paragraphs: [
        'La Cooperativa solicitará autorización previa, expresa e informada del titular, por medios que permitan constancia, salvo los casos en que la ley autorice el tratamiento sin consentimiento (por ejemplo, información requerida por autoridad pública en ejercicio de funciones legales).',
        'La autorización podrá obtenerse por escrito, electrónica, mensaje de datos o cualquier medio que permita concluir de forma razonable que el titular otorgó su consentimiento.',
      ],
    },
    {
      heading: '8. Derechos del titular (Habeas Data)',
      list: [
        'Conocer, actualizar y rectificar sus datos personales frente al responsable o encargados.',
        'Solicitar prueba de la autorización otorgada, salvo cuando expresamente se exceptúe como requisito.',
        'Ser informado sobre el uso dado a sus datos personales.',
        'Presentar quejas ante la Superintendencia de Industria y Comercio por infracciones a la Ley 1581 de 2012.',
        'Revocar la autorización y/o solicitar la supresión del dato cuando no exista un deber legal o contractual que impida eliminarlos.',
        'Acceder de forma gratuita a sus datos personales que hayan sido objeto de tratamiento.',
      ],
    },
    {
      heading: '9. Procedimiento para consultas y reclamos',
      paragraphs: [
        'El titular, o quien actúe por su cuenta, podrá presentar consultas (para conocer información) y reclamos (para corregir, actualizar o suprimir datos, o revocar autorización) ante la Cooperativa.',
        `Consultas: se atenderán en un término máximo de diez (10) días hábiles contados desde la recepción. Si no fuera posible atenderla en ese plazo, se informará al titular los motivos y la fecha en que se responderá, la cual no podrá superar cinco (5) días hábiles adicionales.`,
        `Reclamos: se atenderán en un término máximo de quince (15) días hábiles contados desde el día siguiente a su recepción. De ser necesario prorrogar, se comunicará al titular antes del vencimiento del término inicial; la prórroga no superará ocho (8) días hábiles adicionales.`,
        'El reclamo deberá incluir identificación del titular, descripción de los hechos, documentos de soporte y dirección de notificación. Si estuviere incompleto, se requerirá al interesado dentro de los cinco (5) días siguientes; de no subsanar en dos (2) meses, se entenderá desistido.',
        'Una vez cumplidos los términos sin respuesta, el titular podrá acudir a la SIC: www.sic.gov.co.',
      ],
    },
    {
      heading: '10. Canales de atención',
      paragraphs: [
        `Correo electrónico: ${DATA_PROTECTION_EMAIL}.`,
        `Dirección física: ${DATA_PROTECTION_ADDRESS} (horario de atención institucional).`,
        'Formulario de contacto del portal web en la sección PQRS / Contacto.',
      ],
    },
    {
      heading: '11. Transferencia y transmisión de datos',
      paragraphs: [
        'La Cooperativa podrá transmitir o transferir datos personales a encargados del tratamiento (proveedores de tecnología, hosting, mensajería, etc.) bajo contratos que garanticen el cumplimiento de la ley y esta política.',
        'La transferencia internacional de datos se realizará solo cuando el titular haya dado autorización expresa o cuando se cumplan las condiciones del artículo 26 de la Ley 1581 de 2012 y normas complementarias.',
      ],
    },
    {
      heading: '12. Seguridad de la información',
      paragraphs: [
        'Se implementan medidas técnicas, humanas y administrativas razonables para proteger los datos personales contra adulteración, pérdida, consulta o acceso no autorizado.',
        'El acceso a la información está limitado al personal y proveedores que la requieran para el desempeño de sus funciones, sujetos a deberes de confidencialidad.',
      ],
    },
    {
      heading: '13. Vigencia del tratamiento',
      paragraphs: [
        'Los datos personales se conservarán durante el tiempo necesario para cumplir las finalidades del tratamiento y las obligaciones legales, contractuales y de archivo aplicables a la Cooperativa.',
        'Vencidos los plazos, se procederá a la supresión o anonimización, salvo que exista un deber legal de conservación.',
      ],
    },
    {
      heading: '14. Modificaciones',
      paragraphs: [
        'La Cooperativa podrá actualizar esta política en cualquier momento. Los cambios serán publicados en el sitio web con indicación de la fecha de actualización.',
        'Se recomienda revisar periódicamente este documento.',
      ],
    },
  ],
};
